import { DateTime } from "luxon";
import { ChannelId, EpisodeId, EpisodeRecurrence, EpisodeScheduleType, IEpisode, IInputEpisode, IRecurringEpisodeSchedule } from "../services/extended-program-guide.service";
import { ITime } from "./TimeParser";
import { SafeUrl } from "@angular/platform-browser";
import getRecurringScheduleOccurrences, { IReplicateRecurringScheduleEpisode } from "src/app/features/secured-skeleton/extended-program-guide/utils/getRecurringScheduleOccurrences";

export interface IEpisodeDetailsDialogData {
  episode: IInputEpisode;
  /**
   * If we receive this as `null`, we will understand that the episode is being created. We will understand
   * as it is being edited otherwise.
   */
  id: EpisodeId | null;
  /**
   * existing episodes for overlap comparison
   */
  episodes: ReadonlyArray<Readonly<IEpisode>>;
  /**
   * Channel id to which this episode belongs to
   */
  channelId: ChannelId;
}


type EpisodeViewDataSchedule = IRecurringEpisodeViewData;

export interface IDateTimePair {
  date: Date;
  time: string;
}

export interface IEpisodeDetailsDialogViewData {
  duration?: string;
  showName?: string;
  formattedRecurrence?: string | null;
  title?: string;
  description?: string;
  viewerRating?: number;
  start?: IDateTimePair;
  quality?: number;
  schedule?: EpisodeViewDataSchedule;
  isProcessingThumbnail?: boolean;
  thumbnails?: {
    title: IEpisodeThumbnailInputViewData | null;
    show: IEpisodeThumbnailInputViewData | null;
  };
  isDraggingOverThumbnailElement?: boolean;
  showDetails?: {
    seasonNumber: string;
    episodeNumber: string;
    year: string;
    league: string;
    showType: string;
    closedCaptions: boolean;
    subtitles: boolean;
    cast: string;
    host: string;
  };
}

export interface IEpisodeQualityOption {
  label: string;
  value: number;
}

export interface IEpisodeDetailsErrors {
  start?: {
    date: string | null;
    time: string | null;
  };
  end?: {
    date: string | null;
    time: string | null;
  };
  runsIndefinitely?: string | null;
  duration?: string | null;
  title?: string | null;
  quality?: string | null;
  showName?: string | null;
  viewerRating?: string | null;
  description?: string | null;
}

export function setTime(dateTime: DateTime, value: ITime) {
  let { hours: hour, meridiem } = value;
  if (meridiem !== null) {
    switch (meridiem) {
      case 'AM':
        if (hour === 12) {
          hour = 0;
        }
        break;
      case 'PM':
        if (hour !== 12) {
          hour += 12;
        }
    }
  }
  return dateTime.set({
    hour,
    minute: value.minutes
  });
}

export function getDuration(duration: string) {
  const result = parseFloat(duration);
  if (Number.isNaN(result) || !Number.isFinite(result)) {
    return 'Duration should be a number';
  } else if (result <= 0) {
    return 'Duration should be greater than 0';
  }
  return result;
}

export interface IRecurringEpisodeViewData {
  type?: EpisodeScheduleType.Recurring | EpisodeScheduleType.Definite | EpisodeScheduleType.Indefinite;
  recurrence?: EpisodeRecurrence;
  end?: IDateTimePair;
}

export interface IEpisodeRecurrenceOptionsDialogData {
  startDate: Date;
  duration: number;
  startTime: string;
  schedule: IRecurringEpisodeViewData;
  episodes: ReadonlyArray<Readonly<IEpisode>>;
}

export interface IEpisodeThumbnailInputViewData {
  width: number;
  height: number;
  previewUrl: SafeUrl | string;
  /**
   * original blob URL
   */
  url: string;
  file: File;
}

export interface IThumbnailError {
  message: string;
}

export interface IEpisodeOccurrence {
  startDate: DateTime;
  endDate: DateTime;
  origin: IEpisode;
  schedule: IRecurringEpisodeSchedule | null;
}

export interface IEpisodeError {
  message: string;
}

export type EpisodeOccurrenceInput = IEpisode;

export function getEpisodesOccurrences(episodes: ReadonlyArray<IEpisode>) {
  const occurrences = new Array<IEpisodeOccurrence>();
  for (const ep of episodes) {
    const result = getEpisodeOccurrences(ep);
    if ('type' in result) {
      return result;
    }
    occurrences.push(...result);
  }
  return occurrences;
}

export function getEpisodeOccurrences(episode: EpisodeOccurrenceInput) {
  const occurrences: IEpisodeOccurrence[] = [];
  const startDate = DateTime.fromSeconds(episode.start_date);
  const { schedule } = episode;
  switch (schedule.type) {
    case EpisodeScheduleType.Definite:
      occurrences.push({
        startDate,
        endDate: startDate.plus({
          minutes: episode.duration
        }),
        origin: episode,
        schedule: null
      });
      break;
    case EpisodeScheduleType.Recurring: {
      const result = getRecurringEpisodeOccurrences(episode, schedule);
      if ('type' in result) {
        return result;
      }
      occurrences.push(...result);
      break;
    }
    case EpisodeScheduleType.Indefinite:
      break;
  }
  return occurrences;
}


function getRecurringEpisodeOccurrences(
  episode: EpisodeOccurrenceInput,
  schedule: IRecurringEpisodeSchedule
) {
  const occurrences = new Array<IEpisodeOccurrence>();
  const result = getRecurringScheduleOccurrences(episode, schedule);
  if ('type' in result) {
    return result;
  }
  for (const occurrence of result) {
    occurrences.push({
      startDate: occurrence.startDate,
      endDate: occurrence.endDate,
      origin: episode,
      schedule
    });
  }
  return occurrences;
}

export function validateEpisodeOccurrences(
  existingOccurrences: IEpisodeOccurrence[],
  episode: IReplicateRecurringScheduleEpisode
) {
  const startDate = DateTime.fromSeconds(episode.start_date);
  const endDate = startDate.plus({
    minutes: episode.duration
  });
  for (const occurrence of existingOccurrences) {
    // ! indefinite recurring schedule, it means that the occurrences must be created with max space of end date of the episode passed to this validation function
    /**
     * The `replicateRecurringSchedule` function is used to create the occurrences, and it accepts a `maxSpace` argument, which has
     * an "unknown" default value.
     *
     * This is needed in order to be able to validate, in this episode goes over 2 weeks.
     */
    if (occurrence.schedule !== null && occurrence.schedule.end_date === null) {
    }
    if (
      startDate.toSeconds() <= occurrence.endDate.toSeconds() &&
      startDate.toSeconds() >= occurrence.startDate.toSeconds()
    ) {
      return `This episode start time overlaps with another episode: ${occurrence.origin.title}`;
    } else if (
      endDate.toSeconds() >= occurrence.startDate.toSeconds() &&
      startDate.toSeconds() <= occurrence.startDate.toSeconds()
    ) {
      return `This episode end time overlaps with another episode: ${occurrence.origin.title}`;
    }
  }
  return null;
}